import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Users, CreditCard, LogOut, ShieldCheck, Menu, X, BarChart3, Mail, UserX } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { clsx } from '../../utils/helpers';

const navItems = [
  { label: 'Dashboard', icon: <LayoutDashboard size={18} />, path: '/admin/dashboard' },
  { label: 'Analytics', icon: <BarChart3 size={18} />, path: '/admin/analytics' },
  { label: 'Farms', icon: <Building2 size={18} />, path: '/admin/farms' },
  { label: 'Users', icon: <Users size={18} />, path: '/admin/users' },
  { label: 'Dormant Users', icon: <UserX size={18} />, path: '/admin/dormant-users' },
  { label: 'Subscriptions', icon: <CreditCard size={18} />, path: '/admin/subscriptions' },
  { label: 'Broadcast Email', icon: <Mail size={18} />, path: '/admin/broadcast' },
];

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={22} className="text-emerald-500" />
          <div>
            <p className="text-sm font-bold text-white">PoultryPro</p>
            <p className="text-xs text-gray-500">Platform Admin</p>
          </div>
        </div>
        <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1.5 rounded-lg hover:bg-gray-800 text-gray-400">
          <X size={18} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map(item => {
          const active = location.pathname.startsWith(item.path);
          return (
            <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
              className={clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all',
                active ? 'bg-emerald-600 text-white' : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              )}>
              {item.icon}
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-gray-800">
        <div className="px-3 py-2 mb-1">
          <p className="text-xs font-semibold text-white truncate">{user?.first_name} {user?.last_name}</p>
          <p className="text-xs text-gray-500 truncate">{user?.email}</p>
        </div>
        <button onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-950/50 transition-colors">
          <LogOut size={15} />
          Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-gray-900 border-r border-gray-800 shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile slide-in sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-64 bg-gray-900 shadow-xl z-50">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <header className="lg:hidden bg-gray-900 px-4 py-3 flex items-center gap-3 shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded-lg hover:bg-gray-800 text-gray-300">
            <Menu size={20} />
          </button>
          <ShieldCheck size={18} className="text-emerald-500" />
          <span className="text-sm font-bold text-white">Platform Admin</span>
        </header>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
